// web-front/src/forecast-app/shared/components/dropdowns/client-primary-contact-dropdown/ClientPrimaryContactDropdown.tsx
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {graphql} from 'react-relay';
import {fetchQuery} from 'relay-runtime';
import {ComboBox, Label, FlexColumn} from '@forecast-it/design-system';
import {useIntl} from 'react-intl';
import environment from '../../../../../RelayEnvironment';
import {useInfiniteScroll} from '../../../hooks/useInfiniteScroll';

const ClientPrimaryContactDropdownQuery = graphql`
	query ClientPrimaryContactDropdownQuery($clientId: String, $first: Int, $after: String) {
		viewer {
			company {
				client(id: $clientId) {
					clientContacts(first: $first, after: $after) {
						pageInfo {
							endCursor
							hasNextPage
						}
						edges {
							node {
								id
								firstName
								lastName
							}
						}
					}
				}
			}
		}
	}
`;

const PAGE_SIZE = 20;

// Sentinel id for the non-selectable "no contacts" row shown when the selected client has no
// contacts. Lets the menu open (the ComboBox would otherwise not expand on an empty collection)
// and surfaces an empty-state message (IGNITE-490).
const EMPTY_STATE_KEY = '__no_contacts__';

interface Contact {
	id: string;
	name: string;
}

interface ClientContactNode {
	id: string;
	firstName: string | null;
	lastName: string | null;
}

interface ClientContactsQueryResponse {
	viewer?: {
		company?: {
			client?: {
				clientContacts?: {
					pageInfo: {endCursor: string | null; hasNextPage: boolean};
					edges: Array<{node: ClientContactNode}>;
				};
			};
		};
	};
}

interface ClientPrimaryContactDropdownProps {
	clientId: string;
	value: Contact | null;
	onChange: (contact: Contact | null) => void;
	label?: string;
	// When the dropdown is embedded in a layout that already renders its own label
	// (e.g. the project settings InputLine rows), hide the built-in label.
	hideLabel?: boolean;
	placeholder?: string;
	// Forwarded to the underlying ComboBox as data-cy so the picker can be targeted in tests.
	dataCy?: string;
}

export const ClientPrimaryContactDropdown = ({
	clientId,
	value,
	onChange,
	label,
	hideLabel,
	placeholder,
	dataCy,
}: ClientPrimaryContactDropdownProps) => {
	const {formatMessage} = useIntl();
	const [contacts, setContacts] = useState<Contact[]>(value ? [value] : []);
	const [cursor, setCursor] = useState<string | null>(null);
	const [hasNextPage, setHasNextPage] = useState(true);
	// Tracks whether the first page has resolved, so we don't flash the empty state while loading.
	const [loaded, setLoaded] = useState(false);
	const loadingRef = useRef(false);

	const fetchPage = useCallback(
		(after: string | null) => {
			if (loadingRef.current) return;
			loadingRef.current = true;
			fetchQuery(environment.getInstance(), ClientPrimaryContactDropdownQuery, {
				clientId,
				first: PAGE_SIZE,
				after,
			})
				.toPromise()
				.then(
					(data: ClientContactsQueryResponse | undefined) => {
						const conn = data?.viewer?.company?.client?.clientContacts;
						if (!conn) {
							setLoaded(true);
							loadingRef.current = false;
							return;
						}
						const nodes: Contact[] = conn.edges.map(e => ({
							id: e.node.id,
							name: [e.node.firstName, e.node.lastName].filter(Boolean).join(' '),
						}));
						setContacts(prev => {
							if (after) return [...prev, ...nodes];
							// Preserve any pre-loaded contacts (e.g. the currently-selected primary
							// contact) that aren't returned in the first page of results.
							const nodeIds = new Set(nodes.map(n => n.id));
							const preserved = prev.filter(c => !nodeIds.has(c.id));
							return [...preserved, ...nodes];
						});
						setCursor(conn.pageInfo.endCursor);
						setHasNextPage(conn.pageInfo.hasNextPage);
						setLoaded(true);
						loadingRef.current = false;
					},
					() => {
						setLoaded(true);
						loadingRef.current = false;
					}
				);
		},
		[clientId]
	);

	useEffect(() => {
		setLoaded(false);
		fetchPage(null);
	}, [clientId]);

	const loadMore = useCallback(() => {
		if (!loadingRef.current && hasNextPage) {
			fetchPage(cursor);
		}
	}, [hasNextPage, cursor, fetchPage]);

	const {handleScroll} = useInfiniteScroll({hasNextPage, onLoadMore: loadMore});

	useEffect(() => {
		// The ComboBox renders its listbox inside a React Aria <Overlay> portal (appended to document.body),
		// so scroll events from the <ul role="listbox"> do not bubble to a wrapper div around <ComboBox>.
		// We use a MutationObserver to detect when the portal listbox appears/disappears and
		// attach a native scroll listener directly to it.
		let listboxEl: Element | null = null;

		const attachListener = (el: Element) => {
			listboxEl = el;
			el.addEventListener('scroll', handleScroll as unknown as EventListener);
		};

		const detachListener = () => {
			if (listboxEl) {
				listboxEl.removeEventListener('scroll', handleScroll as unknown as EventListener);
				listboxEl = null;
			}
		};

		const observer = new MutationObserver(() => {
			const found = document.querySelector('[role="listbox"]');
			if (found && found !== listboxEl) {
				detachListener();
				attachListener(found);
			} else if (!found && listboxEl) {
				detachListener();
			}
		});

		observer.observe(document.body, {childList: true, subtree: true});

		return () => {
			observer.disconnect();
			detachListener();
		};
	}, [handleScroll]);

	const dropdownLabel = label ?? formatMessage({id: 'clients.client-contact', defaultMessage: 'Primary Contact'});

	const handleChange = (key: string) => {
		if (!key || key === EMPTY_STATE_KEY) {
			onChange(null);
			return;
		}
		const found = contacts.find(c => c.id === key) ?? null;
		onChange(found);
	};

	// When the selected client has no contacts (first page resolved, nothing returned), show a single
	// non-selectable row so the menu still opens and an empty-state message is visible (IGNITE-490).
	const isEmpty = loaded && contacts.length === 0;
	const items: Contact[] = isEmpty
		? [{id: EMPTY_STATE_KEY, name: formatMessage({id: 'clients.client-contacts-empty-state'})}]
		: contacts;

	const comboBox = (
		<ComboBox
			defaultItems={items}
			value={value?.id ?? undefined}
			onChange={handleChange}
			width={'flex'}
			placeholder={placeholder}
			disabledKeys={[EMPTY_STATE_KEY]}
			data-cy={dataCy}
		>
			{(contact: Contact) => (
				<ComboBox.Item key={contact.id} textValue={contact.name}>
					{contact.name}
				</ComboBox.Item>
			)}
		</ComboBox>
	);

	if (hideLabel) {
		return comboBox;
	}

	return (
		<FlexColumn gap={'s'}>
			<Label>{dropdownLabel}</Label>
			{comboBox}
		</FlexColumn>
	);
};
